import * as FullStory from "@fullstory/browser";
import { useMemo } from "react";
import { EmvDevice, FeatureRole, XolaMobileApp } from "types";
import UAParser from "ua-parser-js";
import { hasFeature } from "../models/Seller";
import { getReleaseInfo } from "../snippets/helpers";
import { DeviceType, UADevice, UAOS } from "../types/Device";
import { getSumologicSessionId } from "../utils/log";
import { useSeller, useUser } from "./useAccount";
import { useCollection } from "./useCollection";

const { userAgent } = navigator;
const ua = new UAParser(userAgent);

const getOS = () => {
    const os: {
        name: string | undefined;
        version: string | undefined;
        displayName?: string;
    } = {
        ...ua.getOS(),
    };

    try {
        (navigator as any).userAgentData.getHighEntropyValues(["platformVersion"]).then((ua: any) => {
            if ((navigator as any).userAgentData.platform === "Windows") {
                const majorPlatformVersion = Number.parseInt(ua.platformVersion.split(".")[0], 10);
                if (majorPlatformVersion >= 13) {
                    os.displayName = "Windows 11 or later";
                } else if (majorPlatformVersion > 0) {
                    os.displayName = "Windows 10";
                }
            }
        });
    } catch {}

    return os;
};

const detectXolaMobileApp = (): XolaMobileApp | null => {
    const userAgent = navigator.userAgent;

    const appPatterns = [
        { name: XolaMobileApp.XolaTablet, pattern: /XolaTablet\/([\d.]+)/ },
        { name: XolaMobileApp.XolaPhone, pattern: /XolaPhone\/([\d.]+)/ },
        { name: XolaMobileApp.XolaRedemption, pattern: /XolaRedemption\/([\d.]+)/ },
        { name: XolaMobileApp.XolaKiosk, pattern: /(?:XolaKiosk|nativeKiosk)\/([\d.]+)/ },
    ];

    for (const { name, pattern } of appPatterns) {
        const match = userAgent.match(pattern);
        if (match) {
            const version = match[1];
            const versionParts = version.split(".").map(Number);
            const minVersion = [2, 0, 0];

            const compareVersions = (a: number[], b: number[]) => {
                for (let index = 0; index < Math.max(a.length, b.length); index++) {
                    const diff = (a[index] || 0) - (b[index] || 0);
                    if (diff !== 0) return diff;
                }
                return 0;
            };

            if (compareVersions(versionParts, minVersion) >= 0) {
                return name;
            }
        }
    }

    return null;
};

export const useSystemInfo = () => {
    const user = useUser();
    const seller = useSeller();
    const { screen, innerWidth, innerHeight, devicePixelRatio } = window;
    return useMemo(() => {
        const [browser, device, os] = [ua.getBrowser(), ua.getDevice(), getOS()];
        return {
            user,
            seller,
            browser,
            device,
            os,
            userAgent: navigator.userAgent,
            currentUrl: window.location.toString(),
            isSeller: seller.id === user.id,
            resolution: {
                window: {
                    width: innerWidth,
                    height: innerHeight,
                },
                desktop: {
                    width: screen.availWidth,
                    height: screen.availHeight,
                },
                native: {
                    width: screen.availWidth * devicePixelRatio,
                    height: screen.availHeight * devicePixelRatio,
                },
            },
        };
    }, [screen.availWidth, screen.availHeight, innerWidth, innerHeight, devicePixelRatio, user, seller]);
};

export const useEmvDeviceInfo = () => {
    const seller = useSeller();
    const hasEMV = hasFeature(seller, FeatureRole.EMV);
    const { data } = useCollection<EmvDevice>(
        ["users", seller.id, "preferences", "stripeTerminal", "paymentDevices"],
        null,
        { enabled: hasEMV },
    );
    return { hasEMV, devices: data };
};

const releaseInfo = getReleaseInfo();

export const appInfo = {
    environment: releaseInfo.env,
    version: releaseInfo.release,
    branch: releaseInfo.branch,
    releaseDate: process.env.RELEASE_DATE ? new Date(Number.parseInt(process.env.RELEASE_DATE, 10) * 1000) : undefined,
};

export const useSupportInfo = () => {
    const fsUrl = FullStory.isInitialized() ? FullStory.getCurrentSessionURL() : undefined;
    const fullStorySessionUrl = fsUrl && fsUrl.length > 0 ? fsUrl : undefined;
    const sumoLogSessionId = getSumologicSessionId();
    return {
        fullStorySessionUrl,
        sumoLogSessionId,
    };
};

export const useDevice = () => {
    const data = { device: ua.getDevice() as UADevice, os: ua.getOS() as UAOS };

    const isTablet = data.device.type === DeviceType.TABLET;
    const isMobile = data.device.type === DeviceType.MOBILE;
    const isTabletOrMobile = isTablet || isMobile;

    const xolaMobileApp = useMemo(() => detectXolaMobileApp(), []);
    const isXolaMobileApp = !!xolaMobileApp;

    return { data, isTablet, isMobile, isTabletOrMobile, xolaMobileApp, isXolaMobileApp };
};
